import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, firstValueFrom, map } from 'rxjs';

import { BaseService } from './base-service';
import { ConfirmationService } from './confirmation.service';
import { isProjectMembership, MembershipsService } from './membership.service';
import { RolesService } from './role.service';
import { UIGroupsService } from './ui-groups.service';
import { UIProjectUsersRefreshService } from './ui-project-users-refresh.service';
import { ListProjectMembers$Params } from '@api/backend-connector/fn/projects/list-project-members';
import {
  BaseProjectMembershipDto,
  BaseProjectUserDto,
  BaseProjectUserDtoPage,
  GroupDto,
} from '@api/backend-connector/models';
import { ProjectsApi } from '@api/backend-connector/services';

export const canProjectUserBeRemoved = (collaborator: BaseProjectUserDto) => {
  // possible if there aren't global/group permissions
  return (
    collaborator.memberId &&
    !collaborator.memberships.find((m) => collaborator.memberId && !isProjectMembership(collaborator.memberId, m))
  );
};

export const canRemoveUserProjectMemberships = (collaborator: BaseProjectUserDto) => {
  // when the user has both project and global/group permissions
  return (
    !canProjectUserBeRemoved(collaborator) &&
    collaborator.memberships.find((m) => m.memberId === collaborator.memberId && m.projectId)
  );
};

type UIProjectUserRole = {
  name: string;
  isUserGlobal: boolean;
  isUserProject: boolean;
  isGroupGlobal: boolean;
  isGroupProject: boolean;
  globalGroups: Record<string, string | undefined>;
  projectGroups: Record<string, string | undefined>;
  allGroups: Record<string, string | undefined>;
};

export type UIEnrichedProjectUser = BaseProjectUserDto & { distinctRoles: UIProjectUserRole[] };

const distinctRoles = (
  memberships: BaseProjectMembershipDto[],
  collaboratorMemberId: string | null | undefined,
  groupsMap: Map<string, GroupDto>,
): UIProjectUserRole[] => {
  if (!collaboratorMemberId) {
    throw new Error('unexpected');
  }
  const isGlobal = (m: BaseProjectMembershipDto) => !m.projectId;
  const result = Object.values(
    memberships.reduce(
      (acc, curr) => {
        if (!acc[curr.role.name]) {
          acc[curr.role.name] = {
            name: curr.role.name,
            isUserGlobal: false,
            isUserProject: false,
            isGroupGlobal: false,
            isGroupProject: false,
            globalGroups: {},
            projectGroups: {},
            allGroups: {},
          };
        }
        const global = isGlobal(curr);
        const isUser = curr.memberId === collaboratorMemberId;

        if (global) {
          if (isUser) {
            acc[curr.role.name].isUserGlobal = true;
          } else {
            acc[curr.role.name].isGroupGlobal = true;
            acc[curr.role.name].globalGroups[curr.memberId] = groupsMap.get(curr.memberId)?.groupName;
            acc[curr.role.name].allGroups[curr.memberId] = groupsMap.get(curr.memberId)?.groupName;
          }
        } else {
          // not global (project)
          if (isUser) {
            acc[curr.role.name].isUserProject = true;
          } else {
            acc[curr.role.name].isGroupProject = true;
            acc[curr.role.name].projectGroups[curr.memberId] = groupsMap.get(curr.memberId)?.groupName;
            acc[curr.role.name].allGroups[curr.memberId] = groupsMap.get(curr.memberId)?.groupName;
          }
        }
        return acc;
      },
      {} as Record<string, UIProjectUserRole>,
    ),
  );
  return result.sort((a, b) => a.name.localeCompare(b.name));
};

const toasts = {
  createSuccessSummary: 'i18n.UIProjectUsersToast.createSuccessSummary',
  createSuccessDetail: 'i18n.UIProjectUsersToast.createSuccessDetail',
  createFailSummary: 'i18n.UIProjectUsersToast.createFailSummary',
  createFailDetail: 'i18n.UIProjectUsersToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.UIProjectUsersToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.UIProjectUsersToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.UIProjectUsersToast.deleteFailSummary',
  deleteFailDetail: 'i18n.UIProjectUsersToast.deleteFailDetail',
};

const confirmMessages = {
  removeUserHeader: 'i18n.UIProjectUser.ConfirmRemoveUser.header',
  removeUserMessage: 'i18n.UIProjectUser.ConfirmRemoveUser.message',
  removeUserProjectMembershipsHeader: 'i18n.UIProjectUser.ConfirmRemoveUserProjectMemberships.header',
  removeUserProjectMembershipsMessage: 'i18n.UIProjectUser.ConfirmRemoveUserProjectMemberships.message',
};

@Injectable({
  providedIn: 'root',
})
export class UIProjectUsersService extends BaseService {
  override toasts = toasts;

  private readonly projectsApi = inject(ProjectsApi);
  private readonly rolesService = inject(RolesService);
  private readonly membershipsService = inject(MembershipsService);
  private readonly uiProjectUsersRefreshService = inject(UIProjectUsersRefreshService);
  private readonly uiGroupsService = inject(UIGroupsService);

  getProjectUser(params: Pick<ListProjectMembers$Params, 'id' | 'userId'>): Observable<BaseProjectUserDto> {
    return this.projectsApi.listProjectMembers(params).pipe(map(({ items }) => items[0]));
  }

  listProjectUsers(params: ListProjectMembers$Params): Observable<BaseProjectUserDtoPage> {
    return this.projectsApi.listProjectMembers(params);
  }

  listEnrichedProjectUsers(
    params: ListProjectMembers$Params,
  ): Observable<BaseProjectUserDtoPage & { items: Array<UIEnrichedProjectUser> }> {
    return combineLatest([this.uiGroupsService.allGroupsMap$, this.listProjectUsers(params)]).pipe(
      map(([groupsMap, page]) => {
        return {
          ...page,
          items: page.items.map((c) => {
            return { ...c, distinctRoles: distinctRoles(c.memberships, c.memberId, groupsMap) };
          }),
        };
      }),
    );
  }

  isProjectUser(params: { projectId: string; memberId: string }) {
    return this.listProjectUsers({ id: params.projectId, memberId: params.memberId }).pipe(
      map((result) => !!result.count),
    );
  }

  async addUser(params: { projectId: string; memberId: string }) {
    const isProjectUser = await firstValueFrom(this.isProjectUser(params));
    if (isProjectUser) {
      throw new Error();
    }

    const allDefaultRoles = await firstValueFrom(this.rolesService.getAllDefaultRoles());
    if (!allDefaultRoles.length) {
      throw new Error();
    }
    await Promise.all(
      allDefaultRoles.map((role) =>
        firstValueFrom(this.membershipsService.createMembership2({ ...params, roleId: role.id })),
      ),
    );
    this.uiProjectUsersRefreshService.refresh$.next();
  }

  async removeUser(params: { projectId: string; projectUser: BaseProjectUserDto }) {
    await Promise.all(
      params.projectUser.memberships
        .filter((m) => !!params.projectUser.memberId && isProjectMembership(params.projectUser.memberId, m))
        .map((membership) => firstValueFrom(this.membershipsService.deleteMembership2({ id: membership.id }))),
    );
    this.uiProjectUsersRefreshService.refresh$.next();
  }

  async openRemoveUserConfirmationDialog(confirmationService: ConfirmationService, label: string) {
    return confirmationService.confirm({
      header: this.translateService.instant(confirmMessages.removeUserHeader),
      message: this.translateService.instant(confirmMessages.removeUserMessage, { label }),
      confirmLabel: this.translateService.instant(this.confirmDelete.confirmLabel),
    });
  }

  async openRemoveUserProjectMembershipsConfirmationDialog(confirmationService: ConfirmationService, label: string) {
    return confirmationService.confirm({
      header: this.translateService.instant(confirmMessages.removeUserProjectMembershipsHeader),
      message: this.translateService.instant(confirmMessages.removeUserProjectMembershipsMessage, { label }),
      confirmLabel: this.translateService.instant(this.confirmDelete.confirmLabel),
    });
  }
}
